import React, { useState } from "react";
import { useUploadPhoto } from "../queries";
import {
  FilePicker,
  compressImage,
  useFileSelect,
} from "@jkast90/kast-common/ui";
import styles from "./PhotoUpload.module.css";
import { errorMessage } from "@jkast90/kast-common/format";
import { logger } from "../utils/logger";

interface PhotoUploadProps {
  folder: "memories" | "profiles" | "imprints";
  onUploadComplete: (photoData: PhotoData) => void;
  onUploadError?: (error: string) => void;
  maxSizeMB?: number;
  acceptedTypes?: string[];
  buttonText?: string;
  showPreview?: boolean;
}

export interface PhotoData {
  s3_key: string;
  url: string;
  filename: string;
  content_type: string;
  file_size: number;
}

/** A picked photo is compressed to 1 MB before it goes up. */
const UPLOAD_MAX_BYTES = 1024 * 1024;

/**
 * The shared FilePicker (drop zone, preview, remove) over kindly's upload: the
 * picked photo is compressed to 1 MB, sent through the photos slice, and
 * handed to onUploadComplete. One photo at a time; a new pick replaces it.
 */
const PhotoUpload: React.FC<PhotoUploadProps> = ({
  folder,
  onUploadComplete,
  onUploadError,
  maxSizeMB = 10,
  acceptedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ],
  buttonText = "Upload Photo",
  showPreview = true,
}) => {
  const uploadPhoto = useUploadPhoto();
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const fileToUpload = await compressImage(file, {
        maxBytes: UPLOAD_MAX_BYTES,
      });
      logger.debug(
        `[PhotoUpload] ${(file.size / 1024 / 1024).toFixed(2)} MB, uploading ${(fileToUpload.size / 1024 / 1024).toFixed(2)} MB`,
      );

      const response = await uploadPhoto.mutateAsync({
        file: fileToUpload,
        folder,
      });

      // Validate response data
      if (!response) {
        throw new Error("No response from server");
      }

      if (!response.s3_key) {
        throw new Error("Invalid response from server - missing s3_key");
      }

      onUploadComplete(response);
    } catch (error: unknown) {
      const message = errorMessage(error, "Failed to upload photo");
      logger.error("[PhotoUpload] Upload failed:", message);
      onUploadError?.(message);
      // Nothing was uploaded: clear the preview.
      select.clear();
    } finally {
      setUploading(false);
    }
  };

  const select = useFileSelect({
    accept: acceptedTypes.join(","),
    maxBytes: maxSizeMB * 1024 * 1024,
    disabled: uploading,
    messages: {
      type: () =>
        `Invalid file type. Accepted types: ${acceptedTypes.join(", ")}`,
      size: () => `File too large. Maximum size: ${maxSizeMB}MB`,
    },
    onReject: ([first]) => onUploadError?.(first.message),
    onFiles: ([file]) => void upload(file),
  });

  return (
    <div className={styles.photoUpload}>
      <FilePicker
        aria-label={buttonText}
        buttonLabel={uploading ? "Uploading..." : buttonText}
        select={select}
        showList={showPreview}
        removeLabel={() => "Remove photo"}
      />
    </div>
  );
};

export default PhotoUpload;
